
    import Vue from "vue";
    import Component from "vue-class-component";
    import { Prop } from "vue-property-decorator";
    import * as toastr from "toastr";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import pagePartList from "@/utilities/PagePartList";

    import ApiButton from "@/vue/components/ApiButton.vue";
    import NumericId from "@/vue/components/NumericId.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";

    import TextPartEdit from "@/vue/components/PageParts/TextPartEdit.vue";
    import NotePartEdit from "@/vue/components/PageParts/NotePartEdit.vue";
    import MapPartEdit from "@/vue/components/PageParts/MapPartEdit.vue";
    import ResourceAndTextPartEdit from "@/vue/components/PageParts/ResourceAndTextPartEdit.vue";
    import RadioChoicePartEdit from "@/vue/components/PageParts/RadioChoicePartEdit.vue";
    import CheckboxChoicesPartEdit from "@/vue/components/PageParts/CheckboxChoicesPartEdit.vue";
    import FreeTextPartEdit from "@/vue/components/PageParts/FreeTextPartEdit.vue";
    import DownloadPartEdit from "@/vue/components/PageParts/DownloadPartEdit.vue";
    import PhotoCapturePartEdit from "@/vue/components/PageParts/PhotoCapturePartEdit.vue";
    import ConfirmToContinuePartEdit from "@/vue/components/PageParts/ConfirmToContinuePartEdit.vue";
    import PasscodeToContinuePartEdit from "@/vue/components/PageParts/PasscodeToContinuePartEdit.vue";

    import { Client } from "@/model/Client";
    import { IPagePart, PagePart } from "@/model/PagePart";
    import { ISaveResponse } from "@/model/ISaveResponse";
    import { PagePartComponent } from "@/model/Enums";
    import { IPagePartOptions } from "@/model/pagePartOptions/IPagePartOptions";

    import { TextOptions } from "@/model/pagePartOptions/TextOptions";
    import { NoteOptions } from "@/model/pagePartOptions/NoteOptions";
    import { MapOptions } from "@/model/pagePartOptions/MapOptions";
    import { ResourceAndTextOptions } from "@/model/pagePartOptions/ResourceAndTextOptions";
    import { RadioChoiceOptions } from "@/model/pagePartOptions/RadioChoiceOptions";
    import { CheckboxChoicesOptions } from "@/model/pagePartOptions/CheckboxChoicesOptions";
    import { FreeTextOptions } from "@/model/pagePartOptions/FreeTextOptions";
    import { DownloadOptions } from "@/model/pagePartOptions/DownloadOptions";
    import { PhotoCaptureOptions } from "@/model/pagePartOptions/PhotoCaptureOptions";
    import { ConfirmToContinueOptions } from "@/model/pagePartOptions/ConfirmToContinueOptions";
    import { PasscodeToContinueOptions } from "@/model/pagePartOptions/PasscodeToContinueOptions";
    import { IMapplicOption, MapplicOption } from "@/model/MapplicOption";

    enum ViewState {
        None = 0,
        ChoosePagePart,
        EditPagePart
    }

    interface IEditComponent extends Vue {
        validate: () => boolean;
    }

    @Component({ components: {
        ApiButton,
        NumericId,
        ConfirmDialogue,
        TextPartEdit,
        NotePartEdit,
        MapPartEdit,
        ResourceAndTextPartEdit,
        RadioChoicePartEdit,
        CheckboxChoicesPartEdit,
        FreeTextPartEdit,
        DownloadPartEdit,
        PhotoCapturePartEdit,
        ConfirmToContinuePartEdit,
        PasscodeToContinuePartEdit } })
    export default class PagePartDialogue extends Vue {

        // need this to be able to see the enum in the template!
        // eslint-disable-next-line
        ViewState: any = ViewState;
        // eslint-disable-next-line
        PagePartComponent: any = PagePartComponent;

        //
        // -- properties
        //

        @Prop({ required: true })
        client!: Client;

        @Prop({ required: true })
        pageID!: string;

        @Prop({ default: true })
        isQuiz!: string;

        @Prop({ default: () => [] })
        mapplicOptionList!: Array<MapplicOption>;

        showDialogue: boolean = false;
        dialogueTitle: string = "";

        pagePartChoices = pagePartList.list.filter(p => p.component!== PagePartComponent.None);

        pagePart: PagePart = new PagePart();

        get viewState(): ViewState {
            if(this.pagePart.component === PagePartComponent.None) return ViewState.ChoosePagePart;
            return ViewState.EditPagePart;
        }

        get pagePartType(): PagePartComponent {
            if(this.pagePart == null) return PagePartComponent.None;
            return this.pagePart.component;
        }

        get pagePartText(): string {
            return pagePartList.getTitle(this.pagePart.component);
        }

        get editComponentName(): string {
            if(this.pagePart == null) return "Empty";
            return PagePartComponent[this.pagePart.component] + "PartEdit";
        }

        get canDelete(): boolean {
            return this.viewState === ViewState.EditPagePart && !this.pagePart.isNew;
        }

        // get pagePartOptions(): IPagePartOptions {
        //     return this.pagePart.options ?? { component: PagePartComponent.None, shouldShowMissingMessage: false, isRedLineQuestion: false, html: "NONE" };
        // }

        //
        // -- methods
        //

        add(): void {
            this.reset();
            this.dialogueTitle = "Add Page Part";
            this.showDialogue = true;
        }

        async edit(id: string): Promise<void> {
            this.reset();
            const pagePartData: IPagePart = await ApiClient.get(`api/pagePart/Load?id=${id}`);
            this.pagePart.update(pagePartData);
            this.dialogueTitle = "Edit " + this.pagePartText;
            this.showDialogue = true;
        }

        choosePart(pagePartChoice: PagePartComponent): void {
            // we've already reset in add() but we need to create options
            this.pagePart.options = this.newOptionsForPart(pagePartChoice);
            // will trigger viewState to change:
            this.pagePart.component = pagePartChoice;
            this.dialogueTitle = "Add " + this.pagePartText;
        }

        reset(): void {
            const pagePartChoice = this.pagePart.component;
            Utils.resetObject(this.pagePart);
            this.pagePart.pageID = this.pageID;
            this.pagePart.options = this.newOptionsForPart(pagePartChoice);
        }

        newOptionsForPart(pagePartComponent: PagePartComponent): IPagePartOptions {
            switch(pagePartComponent) {
                case PagePartComponent.Text: return new TextOptions();
                case PagePartComponent.Note: return new NoteOptions();
                case PagePartComponent.Map: return new MapOptions();
                case PagePartComponent.ResourceAndText: return new ResourceAndTextOptions();
                case PagePartComponent.RadioChoice: {
                    const radioOpts = new RadioChoiceOptions();
                    radioOpts.choices.push("Option One");
                    radioOpts.choices.push("Option Two");
                    return radioOpts;
                }
                case PagePartComponent.CheckboxChoices: {
                    const checkOpts = new CheckboxChoicesOptions();
                    checkOpts.choices.push("Option One");
                    checkOpts.choices.push("Option Two");
                    return checkOpts;
                }
                case PagePartComponent.FreeText: return new FreeTextOptions();
                case PagePartComponent.Download: return new DownloadOptions();
                case PagePartComponent.PhotoCapture: return new PhotoCaptureOptions();
                case PagePartComponent.ConfirmToContinue: return new ConfirmToContinueOptions();
                case PagePartComponent.PasscodeToContinue: return new PasscodeToContinueOptions();
                default: return { component: PagePartComponent.None, shouldShowMissingMessage: false, isRedLineQuestion: false };
            }
        }

        async save(): Promise<void> {
            const editComponent = this.$refs.editComponent as IEditComponent;
            if (!editComponent.validate()) {
                toastr.warning("Please fix highlighted issues", "Cannot Save");
                return;
            }
            const response: ISaveResponse = await ApiClient.post("/api/pagePart/save", this.pagePart, "page-part-save");
            if(this.pagePart.isNew) this.pagePart.id = response.newID;
            toastr.success("Saved");
            this.$emit("saved", this.pagePart);
            this.showDialogue = false;
        }

        confirmDelete(): void {
            const dialog: ConfirmDialogue = this.$refs.deleteDialogue as ConfirmDialogue;
            dialog.show();
        }

        async doDelete(): Promise<void> {
            await ApiClient.post("/api/pagePart/delete", this.pagePart, "page-part-delete");
            toastr.warning("Deleted");
            this.$emit("saved");
            this.showDialogue = false;
        }
    }
